import type { AIQuestionAnswer, PartialAIResponse } from "@frontierx/types";
import { useEffect, useState } from "react";

import { useCallContext } from "../Call/CallContext";
import { CloseTabButton } from "../CloseTabButton";
import { PanelHeader } from "../PanelHeader";
import type { AIQuestionAnswerWithSources } from "./AiHelpCard";
import { AiHelpCard } from "./AiHelpCard";

export type AiHelp =
  | PartialAIResponse
  | AIQuestionAnswer
  | AIQuestionAnswerWithSources;

export function AiHelpBrowser() {
  const {
    aiHistory: history,
    isStreaming,
    streamingResponse,
    streamingStatus,
    handleGetHelp,
    latencyMetrics,
    currentAiHelpRequest,
  } = useCallContext();
  const [currentHelp, setCurrentHelp] = useState<AiHelp | null>(null);
  const [currentIndex, setCurrentIndex] = useState(history.length - 1);

  // When history updates, update the current index and current help
  useEffect(() => {
    if (history.length) {
      setCurrentIndex(history.length - 1);
      setCurrentHelp(history[history.length - 1]);
    }
  }, [history]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setCurrentHelp(history[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentHelp(history[currentIndex + 1]);
    }
  };

  // Convert the current help to AIQuestionAnswerWithSources format
  const helpWithSources: AIQuestionAnswerWithSources | undefined = currentHelp
    ? {
        id: currentHelp.id ?? "",
        question: currentHelp.question ?? "",
        answer: currentHelp.answer ?? "",
        context: currentHelp.context ?? "",
        timestamp:
          "timestamp" in currentHelp ? currentHelp.timestamp : Date.now(),
        isComplete: currentHelp.isComplete ?? false,
        questionId: currentHelp.questionId ?? undefined,
        sourceMaterial:
          "sourceMaterial" in currentHelp
            ? currentHelp.sourceMaterial
            : undefined,
      }
    : undefined;

  // Create a placeholder request object that matches AIQuestionAnswerWithSources
  const requestWithSources: AIQuestionAnswerWithSources | undefined =
    currentAiHelpRequest?.question
      ? {
          id: "",
          question: currentAiHelpRequest.question,
          answer: "",
          context: currentAiHelpRequest.context ?? "",
          timestamp: Date.now(),
          isComplete: false,
          sourceMaterial: undefined,
        }
      : undefined;

  // Convert streaming response to AIQuestionAnswerWithSources format if it exists
  const streamingWithSources: AIQuestionAnswerWithSources | undefined =
    streamingResponse
      ? {
          id: streamingResponse.id ?? "",
          question: streamingResponse.question ?? "",
          answer: streamingResponse.answer ?? "",
          context: streamingResponse.context ?? "",
          timestamp: Date.now(),
          isComplete: streamingResponse.isComplete ?? false,
          questionId: streamingResponse.questionId ?? undefined,
          sourceMaterial: undefined,
        }
      : undefined;

  return (
    <div className="flex flex-col items-center space-y-4">
      <PanelHeader title="Get AI help">
        <div className="flex flex-row items-center justify-between space-x-2">
          <div className="flex-grow-0">
            <CloseTabButton />
          </div>
        </div>
      </PanelHeader>
      <AiHelpCard
        help={streamingWithSources ?? helpWithSources}
        latencyMetrics={latencyMetrics}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        currentIndex={currentIndex}
        historyLength={history.length}
        handleGetHelp={handleGetHelp}
        currentAiHelpRequest={requestWithSources}
        isStreaming={isStreaming}
        streamingStatus={streamingStatus}
      />
    </div>
  );
}
